/**
 * Enterprise Service Registry with Dependency Injection
 * Enables loose coupling between packages through service contracts
 */
export class ServiceRegistry {
    constructor() {
        this.services = new Map();
        this.factories = new Map();
    }
    /**
     * Register a service factory for lazy loading
     * Services are only instantiated when first requested
     */
    registerFactory(serviceName, factory) {
        this.factories.set(serviceName, factory);
        console.info(`🔧 Service factory registered: ${serviceName}`);
    }
    /**
     * Register a pre-instantiated service
     */
    registerService(service) {
        this.services.set(service.serviceName, service);
        console.info(`✅ Service registered: ${service.serviceName} v${service.version}`);
    }
    /**
     * Get service with lazy initialization
     * Returns null if service is not available
     */
    async getService(serviceName) {
        try {
            // Return cached service if available
            if (this.services.has(serviceName)) {
                return this.services.get(serviceName);
            }
            // Try to initialize from factory
            const factory = this.factories.get(serviceName);
            if (!factory) {
                console.warn(`⚠️ Service not available: ${serviceName}`);
                return null;
            }
            console.info(`🚀 Initializing service: ${serviceName}`);
            const service = await factory();
            this.services.set(serviceName, service);
            console.info(`✅ Service initialized: ${serviceName} v${service.version}`);
            return service;
        }
        catch (error) {
            console.error(`❌ Failed to initialize service ${serviceName}:`, error);
            return null;
        }
    }
    /**
     * Check if service is available (registered) without initializing
     */
    isAvailable(serviceName) {
        return this.services.has(serviceName) || this.factories.has(serviceName);
    }
    /**
     * Get list of all registered service names
     */
    getRegisteredServices() {
        return [
            ...this.services.keys(),
            ...this.factories.keys()
        ];
    }
    /**
     * Clear all services (useful for testing or logout)
     * Keeps factories for re-initialization
     */
    clearServices() {
        this.services.clear();
        console.info('🧹 All services cleared');
    }
    /**
     * Clear everything including factories (complete reset)
     */
    reset() {
        this.services.clear();
        this.factories.clear();
        console.info('🔄 Service registry reset');
    }
    /**
     * Get service info for debugging
     */
    getServiceInfo(serviceName) {
        const isInstantiated = this.services.has(serviceName);
        const isRegistered = this.factories.has(serviceName) || isInstantiated;
        const service = this.services.get(serviceName);
        return {
            isInstantiated,
            isRegistered,
            version: service?.version
        };
    }
}
// Singleton instance
export const serviceRegistry = new ServiceRegistry();
//# sourceMappingURL=service-registry.js.map